/**
 * Interface for game settings stored in local storage
 */
export interface GameSettingsData {
  fps: number;
  soundVolume: number;
  musicVolume: number;
  showFps: boolean;
  playerName: string;
}

/**
 * Default settings when none are saved
 */
const DEFAULT_SETTINGS: GameSettingsData = {
  fps: 60,
  soundVolume: 0.7,
  musicVolume: 0.5,
  showFps: false,
  playerName: 'Player'
};

/**
 * Utility class for managing game settings across scenes
 */
export class GameSettings {
  private static instance: GameSettings;
  private settings: GameSettingsData;
  
  private constructor() {
    this.settings = this.loadSettings();
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): GameSettings {
    if (!GameSettings.instance) {
      GameSettings.instance = new GameSettings();
    }
    return GameSettings.instance;
  }
  
  /**
   * Get the current settings
   */
  public getSettings(): GameSettingsData {
    return { ...this.settings };
  }
  
  /**
   * Update a specific setting
   */
  public updateSetting<K extends keyof GameSettingsData>(key: K, value: GameSettingsData[K]): void {
    this.settings[key] = value;
  }
  
  /**
   * Update multiple settings at once
   */
  public updateSettings(newSettings: Partial<GameSettingsData>): void {
    this.settings = { ...this.settings, ...newSettings };
  }
  
  /**
   * Save settings to localStorage
   */
  public saveSettings(): void {
    try {
      localStorage.setItem('cytopodSettings', JSON.stringify(this.settings));
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }
  
  /**
   * Load settings from localStorage
   */
  private loadSettings(): GameSettingsData {
    try {
      const savedSettings = localStorage.getItem('cytopodSettings');
      if (savedSettings) {
        return JSON.parse(savedSettings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    
    return { ...DEFAULT_SETTINGS };
  }
  
  /**
   * Apply FPS setting to the game
   */
  public applyFpsSetting(game: Phaser.Game): void {
    if (game && game.loop) {
      game.loop.targetFps = this.settings.fps;
      console.log(`Applied FPS setting: ${this.settings.fps}`);
    }
  }
  
  /**
   * Check if FPS display is enabled
   */
  public isFpsDisplayEnabled(): boolean {
    return this.settings.showFps;
  }
} 